import React, { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Alert, Button, Col, Form, Row } from "react-bootstrap";
import { MdAdd, MdDelete, MdEdit, MdLocationOn } from "react-icons/md";
import { Pagination } from "@mui/material";
import { AuthContext } from "../../../contexts/AuthContext";
import { tempUrl, useStateContext } from "../../../contexts/ContextProvider";
import { ConfirmDeleteDialog, InfoDialog, Loader, ScrollToTop, SearchBar } from "../../../components";
import { ShowTableEntryPermohonan, StatusChip } from "../../../components/ShowTable";

const statusOptions = [
  { label: "Semua", value: "" },
  { label: "Belum", value: "BELUM" },
  { label: "Acc", value: "ACC" },
  { label: "Reject", value: "REJECT" },
];

const TampilEntryPermohonan = () => {
  const { user, dispatch } = useContext(AuthContext);
  const { screenSize } = useStateContext();
  const navigate = useNavigate();
  const location = useLocation();
  const id = location.pathname.split("/")[2];

  const [isEntryPermohonanExist, setIsEntryPermohonanExist] = useState(false);
  const [namaPemohon, setNamaPemohon] = useState("");
  const [userInput, setUserInput] = useState(null);
  const [status, setStatus] = useState("BELUM");
  const [alasan, setAlasan] = useState("");
  const [fotoKtp, setFotoKtp] = useState([]);
  const [fotoKk, setFotoKk] = useState([]);
  const [fotoPenjamin, setFotoPenjamin] = useState([]);
  const [fotoKendaraan, setFotoKendaraan] = useState([]);
  const [fotoStnkBpkb, setFotoStnkBpkb] = useState([]);
  const [fotoLingkungan, setFotoLingkungan] = useState([]);
  const [fotoLainPendukung, setFotoLainPendukung] = useState([]);
  const [entryPermohonansData, setEntryPermohonansData] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  let [page, setPage] = useState(0);
  const [limit] = useState(20);
  const [pages, setPages] = useState(0);
  const [query, setQuery] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const tipeUser = (user?.tipeUser || "").trim().toUpperCase();
  const isSurveyor = ["SUR", "SURVEYOR"].includes(tipeUser);
  const canEdit =
    !isSurveyor || (status || "BELUM").trim().toUpperCase() === "BELUM";

  useEffect(() => {
    id ? getEntryPermohonanById() : setIsEntryPermohonanExist(false);
  }, [id]);

  useEffect(() => {
    getEntryPermohonans();
  }, [page, searchTerm, statusFilter]);

  const fotoSections = [
    { title: "Foto KTP", data: fotoKtp },
    { title: "KK", data: fotoKk },
    { title: "Penjamin", data: fotoPenjamin },
    { title: "Kendaraan Depan/Belakang", data: fotoKendaraan },
    { title: "STNK & BPKB", data: fotoStnkBpkb },
    { title: "Lingkungan", data: fotoLingkungan },
    { title: "Lain2 Pendukung", data: fotoLainPendukung },
  ];

  const getPhotoUrl = (photo) => {
    return typeof photo === "string" ? photo : photo?.url;
  };

  const getPhotoLocation = (photo) => {
    if (typeof photo === "string" || photo?.latitude == null || photo?.longitude == null) {
      return null;
    }

    return {
      latitude: photo.latitude,
      longitude: photo.longitude,
      accuracy: photo.accuracy,
      capturedAt: photo.capturedAt,
    };
  };

  const formatCapturedAt = (capturedAt) => {
    if (!capturedAt) {
      return "-";
    }

    return new Intl.DateTimeFormat("id-ID", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    }).format(new Date(capturedAt));
  };

  const getGoogleMapsUrl = (location) => {
    return `https://www.google.com/maps?q=${location.latitude},${location.longitude}`;
  };

  const getMapEmbedUrl = (location) => {
    const latitude = Number(location.latitude);
    const longitude = Number(location.longitude);
    const delta = 0.004;
    return `https://www.openstreetmap.org/export/embed.html?bbox=${longitude - delta}%2C${latitude - delta}%2C${longitude + delta}%2C${latitude + delta}&layer=mapnik&marker=${latitude}%2C${longitude}`;
  };

  const allLocatedPhotos = fotoSections
    .flatMap((section) =>
      section.data
        .map((photo) => ({
          title: section.title,
          photo,
          location: getPhotoLocation(photo),
        }))
        .filter((item) => item.location),
    )
    .sort((a, b) => new Date(a.location.capturedAt || 0) - new Date(b.location.capturedAt || 0));

  const lastLocatedPhoto = allLocatedPhotos[allLocatedPhotos.length - 1];

  const handleAuthError = (err) => {
    if (err.response?.status === 401) {
      dispatch({ type: "LOGOUT" });
      navigate("/");
    }
  };

  const handleChange = (e, p) => {
    setPage(p - 1);
  };

  const searchData = (e) => {
    e.preventDefault();
    setPage(0);
    setSearchTerm(query);
  };

  const changeStatusFilter = (value) => {
    setPage(0);
    setStatusFilter(value);
  };

  const getEntryPermohonans = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${tempUrl}/permohonansPagination?status=${statusFilter}&search_query=${encodeURIComponent(searchTerm)}&page=${page}&limit=${limit}`,
        {
          id: user._id,
          token: user.token,
        },
      );
      setQuery(searchTerm);
      setEntryPermohonansData(response.data.permohonans);
      setPage(response.data.page);
      setPages(response.data.totalPage);
    } catch (err) {
      handleAuthError(err);
      setError(err.response?.data?.message || "Gagal mengambil data permohonan.");
    }
    setLoading(false);
  };

  const getEntryPermohonanById = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${tempUrl}/permohonans/${id}`, {
        id: user._id,
        token: user.token,
      });
      setIsEntryPermohonanExist(true);
      setNamaPemohon(response.data.namaPemohon);
      setUserInput(response.data.userId || null);
      setStatus(response.data.status || "BELUM");
      setAlasan(response.data.alasan || "");
      setFotoKtp(response.data.fotoKtp || []);
      setFotoKk(response.data.fotoKk || []);
      setFotoPenjamin(response.data.fotoPenjamin || []);
      setFotoKendaraan(response.data.fotoKendaraan || []);
      setFotoStnkBpkb(response.data.fotoStnkBpkb || []);
      setFotoLingkungan(response.data.fotoLingkungan || []);
      setFotoLainPendukung(response.data.fotoLainPendukung || []);
    } catch (err) {
      handleAuthError(err);
      setError(err.response?.data?.message || "Gagal mengambil detail permohonan.");
    }
    setLoading(false);
  };

  const deleteEntryPermohonan = async () => {
    setShowDeleteConfirm(false);
    setLoading(true);
    try {
      await axios.post(`${tempUrl}/deletePermohonan/${id}`, {
        id: user._id,
        token: user.token,
      });
      navigate("/permohonan");
      await getEntryPermohonans();
    } catch (err) {
      handleAuthError(err);
      setDeleteError(err.response?.data?.message || "Gagal menghapus permohonan.");
      setLoading(false);
    }
  };

  const renderFotoGroup = (title, data) => {
    return (
      <section className="section-panel mt-3" key={title}>
        <h2 className="h6 fw-bold mb-3">{title}</h2>
        {data.length === 0 && <div className="text-muted">Belum ada foto.</div>}
        <div className="photo-grid">
          {data.map((img, i) => {
            const photoUrl = getPhotoUrl(img);
            const photoLocation = getPhotoLocation(img);

            return (
              <div className="photo-card" key={`${title}-${i}`}>
                <button type="button" onClick={() => window.open(photoUrl, "_blank", "noopener,noreferrer")}>
                  <img src={photoUrl} alt={`${title} ${i + 1}`} />
                  <span>No. {i + 1}</span>
                </button>
                <div className="photo-meta">
                  <div>
                    <strong>Waktu</strong>
                    <span>{formatCapturedAt(img?.capturedAt)}</span>
                  </div>
                </div>
                {photoLocation && (
                  <Button
                    className="photo-map-btn"
                    size="sm"
                    variant="outline-primary"
                    type="button"
                    onClick={() => window.open(getGoogleMapsUrl(photoLocation), "_blank", "noopener,noreferrer")}
                  >
                    <MdLocationOn /> Lihat Maps
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      </section>
    );
  };

  const renderMapLastLocation = () => {
    if (!lastLocatedPhoto) {
      return null;
    }

    return (
      <section className="section-panel">
        <div className="toolbar mt-0">
          <div>
            <h2 className="h6 fw-bold mb-1">Lokasi Foto Terakhir</h2>
            <div className="text-muted">
              {lastLocatedPhoto.title}
              {lastLocatedPhoto.location.accuracy ? ` - Akurasi ${Math.round(lastLocatedPhoto.location.accuracy)} meter` : ""}
            </div>
          </div>
          <Button
            className="btn-icon"
            size="sm"
            variant="outline-primary"
            onClick={() => window.open(getGoogleMapsUrl(lastLocatedPhoto.location), "_blank", "noopener,noreferrer")}
          >
            <MdLocationOn /> Google Maps
          </Button>
        </div>
        <div className="map-preview mt-3">
          <iframe
            title="Lokasi Foto Terakhir"
            src={getMapEmbedUrl(lastLocatedPhoto.location)}
            loading="lazy"
          />
        </div>
      </section>
    );
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <div>
      <ScrollToTop />
      <div className="page-header">
        <div>
          <div className="page-kicker">Survey</div>
          <h1 className="page-title">Entry Permohonan</h1>
        </div>
        <Button className="btn-icon" size="sm" onClick={() => navigate("/permohonan/tambahPermohonan")}>
          <MdAdd /> Tambah
        </Button>
      </div>
      {error && <Alert variant="warning">{error}</Alert>}

      {isEntryPermohonanExist && (
        <>
          <section className="section-panel">
            <div className="toolbar mt-0">
              <div className="detail-title-actions">
                <Button className="btn-back" size="sm" variant="outline-secondary" onClick={() => navigate("/permohonan")}>
                  {"< Kembali"}
                </Button>
                <div>
                  <h2 className="h6 fw-bold mb-1">Detail Entry Permohonan</h2>
                  <div className="text-muted">{namaPemohon}</div>
                </div>
              </div>
              <div className="d-flex gap-2 flex-wrap">
                {canEdit && (
                  <Button className="btn-icon" size="sm" variant="outline-primary" onClick={() => navigate(`/permohonan/${id}/edit`)}>
                    <MdEdit /> Ubah
                  </Button>
                )}
                {!isSurveyor && (
                  <Button className="btn-icon" size="sm" variant="outline-danger" onClick={() => setShowDeleteConfirm(true)}>
                    <MdDelete /> Hapus
                  </Button>
                )}
              </div>
            </div>
            <hr />
            <Row>
              <Col lg={6} md={8}>
                <div className="mb-3">
                  <div className="detail-label">Nama Pemohon</div>
                  <div className="detail-value">{namaPemohon || "-"}</div>
                </div>
                <div className="mb-3">
                  <div className="detail-label">Surveyor</div>
                  <div className="detail-value">
                    {userInput?.kodeSurveyor?.kodeSurveyor
                      ? `${userInput.kodeSurveyor.kodeSurveyor} - ${userInput.kodeSurveyor.namaSurveyor}`
                      : userInput?.username || "-"}
                  </div>
                </div>
                <div className="mb-3">
                  <div className="detail-label">Status</div>
                  <div className="detail-value">{status}</div>
                </div>
                <div className="mb-3">
                  <div className="detail-label">Alasan</div>
                  <div className="detail-value">{alasan || "-"}</div>
                </div>
              </Col>
            </Row>
          </section>
          {renderMapLastLocation()}
          {fotoSections.map((section) => renderFotoGroup(section.title, section.data))}
        </>
      )}

      <section className="section-panel">
        <Row>
          <Col lg={6}>
            <div className="mb-3">
              <div className="detail-label mb-2">Filter Status</div>
              <div className="d-flex flex-wrap gap-3">
                {statusOptions.map((option) => (
                  <Form.Check
                    key={option.label}
                    inline
                    type="radio"
                    name="entryStatusFilter"
                    id={`entry-status-filter-${option.label}`}
                    label={option.label}
                    checked={statusFilter === option.value}
                    onChange={() => changeStatusFilter(option.value)}
                  />
                ))}
              </div>
            </div>
          </Col>
        </Row>
        <Form onSubmit={searchData}>
          <div className="search-row mt-0">
            <SearchBar value={query} setSearchTerm={setQuery} />
            <Button type="submit">Cari</Button>
          </div>
        </Form>
      </section>
      <div className="status-legend">
        <span className="status-legend-title">Keterangan Status</span>
        <div className="status-legend-items">
          <StatusChip statusValue="BELUM" />
          <StatusChip statusValue="ACC" />
          <StatusChip statusValue="REJECT" />
        </div>
      </div>
      <ShowTableEntryPermohonan currentPosts={entryPermohonansData} />
      <div className="search-row">
        <Pagination
          count={pages}
          page={page + 1}
          onChange={handleChange}
          color="primary"
          size={screenSize <= 600 ? "small" : "large"}
        />
      </div>
      <ConfirmDeleteDialog
        show={showDeleteConfirm}
        message={`Yakin ingin menghapus permohonan ${namaPemohon}?`}
        onCancel={() => setShowDeleteConfirm(false)}
        onConfirm={deleteEntryPermohonan}
      />
      <InfoDialog
        show={Boolean(deleteError)}
        title="Data Tidak Bisa Dihapus"
        message={deleteError}
        onClose={() => setDeleteError("")}
      />
    </div>
  );
};

export default TampilEntryPermohonan;
